import { useMemo, useRef, useCallback, useEffect } from 'react';
import { mapValues, pickBy } from 'lodash';
import {
  useQueryParams,
  useQueryParam,
  withDefault,
  StringParam,
  NumberParam,
  DecodedValueMap,
  QueryParamConfigMap,
} from 'use-query-params';

import { useAppState } from 'containers/ApplicationStateContext';
import useUniqueCallback from 'utils/hooks/useUniqueCallback';
import useOnChange from 'utils/hooks/useOnChange';
import useTableSorting from 'utils/hooks/useTableSorting';
import { getUndefinedIfNoKeys, pickBySchema } from 'utils/object';

interface FilterSearchSortingAndPaginationOptions {
  filterSchema: QueryParamConfigMap;
  sortOptions: Parameters<typeof useTableSorting>;
  variant?: string;
  storeKey?: string;
}

type FilterValues = Partial<DecodedValueMap<QueryParamConfigMap>>;

export const useFilterSearchSortingAndPagination = ({
  filterSchema,
  sortOptions,
  variant,
  storeKey: key,
}: FilterSearchSortingAndPaginationOptions) => {
  const sorting = useTableSorting(...sortOptions);
  const clearSearchRef = useRef<() => void | null>();
  const clearFilterRef = useRef<() => void | null>();

  const [pageParam, setPageParam] = useQueryParam('page', withDefault(NumberParam, 1));
  const [perPageParam, setPerPageParam] = useQueryParam(
    'limit',
    withDefault(NumberParam, Number(localStorage.getItem('tableLimitPerPage')) || 25)
  );
  const [internalFilterParams, setInternalFilterParams] = useQueryParams(filterSchema);
  const [searchParam, setSearchParam] = useQueryParam('search', withDefault(StringParam, ''));
  const [searchModeParam] = useQueryParam('searchMode', withDefault(StringParam, 'metadata'));

  const { storedValues, setStoredValue } = useAppState();

  const page = useMemo(
    () => (key ? storedValues[`${key}${variant || ''}Page`] ?? 1 : pageParam),
    [key, variant, storedValues, pageParam]
  );

  const setPage = useCallback(
    (value: number | undefined) =>
      key ? setStoredValue({ key: `${key}${variant || ''}Page`, value }) : setPageParam(value),
    [key, variant, setStoredValue, setPageParam]
  );

  const perPage = useMemo(
    () =>
      key
        ? storedValues[`${key}PerPage`] ?? (Number(localStorage.getItem('tableLimitPerPage')) || 25)
        : perPageParam,
    [key, storedValues, perPageParam]
  );

  const setPerPage = useCallback(
    (value: number) =>
      key ? setStoredValue({ key: `${key}PerPage`, value }) : setPerPageParam(value),
    [key, setStoredValue, setPerPageParam]
  );

  const deepLink = useMemo(() => {
    if (!key) {
      return null;
    }
    const filter = pickBy(internalFilterParams, (value) => value != null);
    if (Object.keys(filter).length === 0 && !searchParam) {
      return null;
    }

    return { filter, search: searchParam };
  }, [key, internalFilterParams, searchParam]);

  useEffect(() => {
    if (!key || !deepLink) {
      return;
    }
    setStoredValue({ key: `${key}Filter`, value: deepLink.filter });
    setStoredValue({ key: `${key}Search`, value: deepLink.search });
    setInternalFilterParams(
      mapValues(filterSchema, () => undefined),
      'replaceIn'
    );
    setSearchParam(undefined, 'replaceIn');
  }, [key, deepLink, filterSchema, setStoredValue, setInternalFilterParams, setSearchParam]);

  const internalFilter = useMemo(() => {
    if (!key) {
      return internalFilterParams;
    }

    return deepLink?.filter ?? storedValues[`${key}Filter`] ?? {};
  }, [key, deepLink, storedValues, internalFilterParams]);

  const setInternalFilter = useCallback(
    (value: FilterValues) =>
      key ? setStoredValue({ key: `${key}Filter`, value }) : setInternalFilterParams(value),
    [key, setStoredValue, setInternalFilterParams]
  );

  const search = useMemo(() => {
    if (!key) {
      return searchParam;
    }

    return deepLink?.search ?? storedValues[`${key}Search`] ?? '';
  }, [key, deepLink, storedValues, searchParam]);

  const setSearch = useCallback(
    (value: string) =>
      key ? setStoredValue({ key: `${key}Search`, value }) : setSearchParam(value),
    [key, setStoredValue, setSearchParam]
  );

  const searchMode = useMemo(
    () => (key ? storedValues[`${key}SearchMode`] ?? 'metadata' : searchModeParam),
    [key, storedValues, searchModeParam]
  );

  const setFilter = useUniqueCallback(
    (value: Maybe<Partial<typeof filter>>) => {
      if (value == null) {
        // FIXME: Fix type returned from mapValues (can this ever happen?)
        return setInternalFilter(mapValues(internalFilter, () => value) as any);
      }
      return setInternalFilter({ ...internalFilter, ...value });
    },
    [internalFilter, setInternalFilter]
  );

  // Derive the search values
  const derived = useMemo(
    () => [search, ...Object.values(internalFilter)].join(','),
    [search, internalFilter]
  );

  // When filtering or searching - redirect a user to page 1
  useOnChange(derived, () => page > 1 && setPage(undefined), undefined, false);

  const clearSearch = useCallback(() => {
    setSearch('');
    clearSearchRef.current?.();
  }, [setSearch]);

  const clearFilter = useCallback(() => {
    setFilter(undefined);
    clearFilterRef.current?.();
  }, [setFilter]);

  const clearAll = useCallback(() => {
    clearSearch();
    clearFilter();
  }, [clearSearch, clearFilter]);

  const filter = getUndefinedIfNoKeys(pickBySchema(internalFilter, filterSchema));

  const memoized = useMemo(
    () => ({
      page,
      setPage,
      perPage,
      setPerPage,
      filter,
      setFilter,
      search,
      setSearch,
      sorting,
      clearSearchRef,
      clearFilterRef,
      clearSearch,
      clearFilter,
      clearAll,
      isFiltered: !!search || !!filter,
      searchMode,
    }),
    [
      clearAll,
      clearFilter,
      clearSearch,
      filter,
      page,
      perPage,
      search,
      searchMode,
      setFilter,
      setPage,
      setPerPage,
      setSearch,
      sorting,
    ]
  );

  return memoized;
};
